import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  sameFieldsValidator,
  validPassphraseValidator,
} from '@app/components/commons/forms/formly-validators';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import {
  AcceptPolicyPayload,
  EmissionMigrationPayload,
  RecoveryMigrationPayload,
} from '@app/services/migration/migration.models';
import { MigrationeService } from '@app/services/migration/migration.service';
import { SessionService } from '@app/services/session/session.service';
import { UserCertificate, UserSession } from '@app/store/models';
import { Migration } from '@app/store/models/session.model';
import { selectSession } from '@app/store/selectors/session.selector';
import { GlobalState } from '@app/store/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, combineLatest, lastValueFrom, tap } from 'rxjs';

interface MigrationStep {
  step: number;
  title: string;
  description: string;
  open?: boolean;
  disabled?: boolean;
}
@UntilDestroy()
@Component({
  selector: 'app-migration',
  templateUrl: './migration.component.html',
  styleUrls: ['./migration.component.scss'],
  standalone: false,
})
export class MigrationComponent implements OnInit {
  public migrationSuccessModalVisible = false;
  session: UserSession | undefined;
  certificate: UserCertificate | undefined;
  loading = false;
  CF: string = '';
  modalMessageType: 'info' | 'warning' = 'info';
  /*settata a true solo quando la migrazione è completata con successo */
  isMigrationCompleted = false;

  migrationStatus: Migration = undefined;
  userSession$: Observable<UserSession>;
  currentStep: number = 0;
  consentAttempted: boolean = false;
  otpAttempted: boolean = false;
  newPinAttempted: boolean = false;
  passphraseAttempted: boolean = false;
  newOtpAttempted: boolean = false;

  constructor(
    private store: Store<GlobalState>,
    private sessionService: SessionService,
    private certificateService: CertificateService,
    private otpService: OtpService,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService,
    private migrationService: MigrationeService
  ) {
    this.userSession$ = this.store.select(selectSession);
    this.migrationService.migrationModalVisible$
      .pipe(untilDestroyed(this))
      .subscribe(visible => {
        if (visible) {
          // Delay to allow current change detection cycle to settle
          setTimeout(() => this.resetWizardState());
        }
      });
  }

  ngOnInit(): void {
    //al cambio della sessione o della sessione di migrazione
    combineLatest([this.userSession$])
      .pipe(untilDestroyed(this))
      .subscribe(([userSession]) => {
        this.migrationStatus = userSession.migrationStatus;
        console.log(
          '🚀 ~ MigrationComponent ~ .subscribe ~ migrationStatus:',
          this.migrationStatus
        );
      });

    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.session = store.session;

            console.log(
              '🚀 ~ MigrationComponent ~ ngOnInit ~  this.migrationStatus:',
              this.migrationStatus
            );

            this.getMigrationStep();

            // this.session.migration_state = MigrationState.PrivacyAccepted;
            if (store.certificate) {
              this.certificate = store.certificate;
            }
          }
          if (store.certificate) {
            if (
              store.certificate.certificateInfo.subjectDN.SERIALNUMBER.includes(
                'TINIT-'
              )
            ) {
              this.CF =
                store.certificate.certificateInfo.subjectDN.SERIALNUMBER.slice(
                  6
                );
            }
          }
        })
      )
      .subscribe();

    this.setupValidationAttemptReset();
  }

  private setupValidationAttemptReset(): void {
    this.userAcceptedGeneralConditions.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.consentAttempted) this.consentAttempted = false;
      });

    this.pin.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.consentAttempted) this.consentAttempted = false;
    });

    this.otp.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.otpAttempted) this.otpAttempted = false;
    });

    this.newPin.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.newPinAttempted) this.newPinAttempted = false;
    });

    this.confirmPin.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.newPinAttempted) this.newPinAttempted = false;
    });

    this.passphrase.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.passphraseAttempted) this.passphraseAttempted = false;
    });

    this.newOtp.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.newOtpAttempted) this.newOtpAttempted = false;
    });
  }

  ngAfterViewInit(): void {
    this.modalMessageType = this.migrationService.checkMigrationModalType();
  }

  MigrationSteps: MigrationStep[] = [
    {
      step: 1,
      title: 'MIGRATION_PAGE.STEP_1_CONSENT.TITLE',
      description: 'MIGRATION_PAGE.STEP_1_CONSENT.DESCRIPTION',
      //open: true,
      disabled: true,
    },
    {
      step: 2,
      title: 'MIGRATION_PAGE.STEP_2_5_CONFIRM_OPERATION.TITLE',
      description: 'MIGRATION_PAGE.STEP_2_5_CONFIRM_OPERATION.DESCRIPTION',
      disabled: true,
    },
    {
      step: 3,
      title: 'MIGRATION_PAGE.STEP_3_NEW_PIN.TITLE',
      description: 'MIGRATION_PAGE.STEP_3_NEW_PIN.DESCRIPTION',
      disabled: true,
    },
    {
      step: 4,
      title: 'MIGRATION_PAGE.STEP_4_PASSPHRASE.TITLE',
      description: 'MIGRATION_PAGE.STEP_4_PASSPHRASE.DESCRIPTION',
      disabled: true,
    },
    {
      step: 5,
      title: 'MIGRATION_PAGE.STEP_2_5_CONFIRM_OPERATION.TITLE',
      description: 'MIGRATION_PAGE.STEP_2_5_CONFIRM_OPERATION.DESCRIPTION',
      disabled: true,
    },
  ];

  //Apro lo step corrente e chiudo gli altri
  openStep(step: number) {
    this.MigrationSteps.forEach((migrationStep, index) => {
      if (index === step) {
        migrationStep.open = true;
      } else {
        migrationStep.open = false;
      }
    });
    if (step > -1) {
      this.currentStep = step;
    }
  }

  getMigrationStep() {
    //Se la migrazione è completata apro il messaggio di successo e chiudo tutto
    if (this.isMigrationCompleted) {
      this.openStep(-1);
      return;
    } else {
      switch (this.session.migrationStatus) {
        //è disponibile la migrazione quindi lo porto al primo step
        case Migration.AVAILABLE:
          this.openStep(0);
          break;
        //l'user ha accettato le policy quindi lo porto al secondo step
        case Migration.ACCEPTED:
          this.openStep(2);
          break;
        //è fallita la chiamata emissionMigration e quindi devo fare il recovery, lo porto al quarto step (saltando il pin)
        case Migration.TO_FINALIZE:
          this.openStep(3);
          break;
        default:
          //default è il primo step
          this.openStep(0);
          break;
      }
    }
  }

  isStepComplete(step: number) {
    switch (step + 1) {
      case 1:
        return this.consentForm.valid || this.currentStep > 0;
      case 2:
        return this.otpForm.valid || this.currentStep > 1;
      case 3:
        return this.newPinForm.valid || this.currentStep > 2;
      case 4:
        return this.passphraseForm.valid || this.currentStep > 3;
      case 5:
        return this.newOtpForm.valid || this.currentStep > 4;
      default:
        return false;
    }
  }

  get personalData() {
    const nome = this.certificate.certificateInfo.subjectDN.CN;
    const iut = this.certificate.certificateInfo.subjectDN.dnQualifier;
    const date = moment(this.certificate.validityTo.substring(0, 10)).format(
      'DD/MM/YYYY'
    );

    return {
      nome,
      iut,
      date,
    };
  }

  /**CAMPI FORM */

  //STEP_1_CONSENT FORM CONSENT
  userAcceptedGeneralConditions = new UntypedFormControl(false, [
    //per i checkbox va usato requiredTrue
    Validators.requiredTrue,
  ]);

  pin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  consentForm = new UntypedFormGroup({
    userAcceptedGeneralConditions: this.userAcceptedGeneralConditions,
    pin: this.pin,
  });

  //STEP_2_OTP FORM

  otp = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  otpForm = new UntypedFormGroup({
    otp: this.otp,
  });

  //STEP_3_NEW_PIN FORM
  newPin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  confirmPin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  newPinForm = new UntypedFormGroup(
    {
      newPin: this.newPin,
      confirmPin: this.confirmPin,
    },
    {
      validators: [sameFieldsValidator('newPin', 'confirmPin')],
      updateOn: 'change',
    }
  );

  //STEP_4_PASSPHRASE FORM

  passphrase = new UntypedFormControl('', [
    Validators.required,
    validPassphraseValidator,
  ]);

  passphraseForm = new UntypedFormGroup({
    passphrase: this.passphrase,
  });

  //STEP_5_OTP FORM

  newOtp = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  newOtpForm = new UntypedFormGroup({
    newOtp: this.newOtp,
  });

  // convenience getter for easy access to form fields
  get fContractSign() {
    return this.consentForm.controls;
  }

  get contractSignFormErrors() {
    const errors = this.consentForm.errors;
    return errors;
  }

  get fPassphrase() {
    return this.passphraseForm.controls;
  }

  get fNewPin() {
    return this.newPinForm.controls;
  }

  get newPinFormErrors() {
    const errors = this.newPinForm.errors;
    return errors;
  }

  hasNewPinsAtMinLength(minLength: number = 8): boolean {
    const newPinValue = (this.fNewPin['newPin']?.value ?? '').toString();
    const confirmPinValue = (this.fNewPin['confirmPin']?.value ?? '').toString();
    return newPinValue.length >= minLength && confirmPinValue.length >= minLength;
  }

  get otpFormErrors() {
    const errors = this.otpForm.errors;
    return errors;
  }

  get newOtpFormErrors() {
    const errors = this.newOtpForm.errors;
    return errors;
  }

  onFormConsentSubmit() {
    //TODO: da implementare
    console.log(this.consentForm.value);
  }
  //END FORM-CONSENT

  next(currentIndex: number, force: boolean = false) {
    if (!force) {
      const currentForm = this.getStepForm(currentIndex);
      if (currentForm?.invalid) {
        if (currentIndex === 0) {
          this.consentAttempted = true;
        } else if (currentIndex === 1) {
          this.otpAttempted = true;
        } else if (currentIndex === 2) {
          this.newPinAttempted = true;
        } else if (currentIndex === 3) {
          this.passphraseAttempted = true;
        } else if (currentIndex === 4) {
          this.newOtpAttempted = true;
        }
        currentForm.markAllAsTouched();
        currentForm.updateValueAndValidity({ emitEvent: false });
        return;
      }
    }
    if (currentIndex === 0) {
      this.consentAttempted = false;
    } else if (currentIndex === 1) {
      this.otpAttempted = false;
    } else if (currentIndex === 2) {
      this.newPinAttempted = false;
    } else if (currentIndex === 3) {
      this.passphraseAttempted = false;
    } else if (currentIndex === 4) {
      this.newOtpAttempted = false;
    }

    //li esegue sempre in questi step
    if (currentIndex === 0 || currentIndex === 3) {
      this.sendOtp();
    }

    //click su secondo accordion, prima chiamo servizio per accettare policy
    if (!force && currentIndex === 1) {
      this.acceptPrivacyPolicy();
    }
    // click su quinto e ultimo accordion, chiamo servizio per migrare
    else if (!force && currentIndex === 4) {
      console.log(
        '🚀 ~ MigrationComponent ~ next ~ this.isRecovery:'
        //this.isRecovery
      );
      if (this.session.migrationStatus === Migration.TO_FINALIZE) {
        //è in modalità recovery, perchè è fallita la chiamata emissionMigration
        this.recoveryMigration();
      } else {
        this.emissionMigration();
      }
    } else {
      //è forzato
      //chiudo accordion corrente e apro il successivo
      this.MigrationSteps[currentIndex].open = false;
      if (currentIndex + 1 < this.MigrationSteps.length) {
        this.openStep(currentIndex + 1);
      }
    }
  }

  private getStepForm(currentIndex: number): UntypedFormGroup | null {
    switch (currentIndex) {
      case 0:
        return this.consentForm;
      case 1:
        return this.otpForm;
      case 2:
        return this.newPinForm;
      case 3:
        return this.passphraseForm;
      case 4:
        return this.newOtpForm;
      default:
        return null;
    }
  }

  //Chiamate a servizio primi 2 steps
  acceptPrivacyPolicy() {
    this.loading = true;
    const text = this.translateService.instant(
      'RENEW_PAGE.CONTRACT_SIGN_STEP.CONTRACT_CONDITION'
    );

    const pin = this.consentForm.value.pin.trim();
    const otp = this.otpForm.value.otp.trim();

    const acceptPolicyPayload: AcceptPolicyPayload = {
      text,
      pin,
      otp,
    };

    console.log(
      '🚀 ~ MigrationComponent ~ acceptPrivacyPolicy ~ acceptPolicyPayload:',
      acceptPolicyPayload
    );

    this.migrationService.acceptPrivacyPolicy(acceptPolicyPayload).subscribe({
      next: async response => {
        console.log('🚀 ~ ForcePWDComponent ~ onSubmit ~ response', response);
        sessionStorage.setItem('migration', Migration.ACCEPTED);
        this.next(1, true);
      },
      error: error => {
        console.log(
          '🚀 ~ MigrationComponent ~ acceptPrivacyPolicy ~ error',
          error
        );
        this.handleStepError(1, 0);
        this.refreshSessionData();
      },
      complete: () => {
        this.loading = false;
        this.refreshSessionData();
      },
    });
  }

  //Chiamate a servizio ultimi 3 steps
  //SERVICE 2 EMISSIONS
  emissionMigration() {
    this.loading = true;
    const passphrase = this.passphraseForm.value.passphrase;

    const pin = this.newPinForm.value.newPin.trim();
    const otp = this.newOtpForm.value.newOtp.trim();

    const emissionMigrationPayload: EmissionMigrationPayload = {
      passphrase,
      pin,
      otp,
    };

    console.log(
      '🚀 ~ MigrationComponent ~ emissionMigration ~ emissionMigration:',
      emissionMigrationPayload
    );

    this.migrationService
      .emissionMigration(emissionMigrationPayload)
      .subscribe({
        next: async response => {
          console.log('🚀 ~ ForcePWDComponent ~ onSubmit ~ response', response);
          //migrazione con successo
          this.completeMigration();
        },
        error: error => {
          console.log(
            '🚀 ~ MigrationComponent ~ acceptPrivacyPolicy ~ error',
            error
            //controllare codice errore
          );

          this.setFailedMigration(error);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  //Service 3 MIGRATION RECOVERY
  recoveryMigration() {
    this.loading = true;
    const passphrase = this.passphraseForm.value.passphrase;
    const otp = this.newOtpForm.value.newOtp.trim();

    const recoveryMigrationPayload: RecoveryMigrationPayload = {
      passphrase,
      otp,
    };

    console.log(
      '🚀 ~ MigrationComponent ~ recoveryMigration ~ recoveryMigration:',
      recoveryMigrationPayload
    );

    this.migrationService
      .ricoveryMigration(recoveryMigrationPayload)
      .subscribe({
        next: async response => {
          console.log('🚀 ~ ForcePWDComponent ~ onSubmit ~ response', response);
          //migrazione con successo
          this.completeMigration();
        },
        error: error => {
          console.log(
            '🚀 ~ MigrationComponent ~ acceptPrivacyPolicy ~ error',
            error
          );
          this.handleStepError(4, 3);
          this.refreshSessionData();
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  //è fallita la chiamata emissionMigration
  setFailedMigration(error: any) {
    //se è fallita la chiamata emissionMigration
    //se il codice errore è MT_MIG_001 allora devo fare la recovery
    const fallbackStep =
      error?.error?.code === 'MT_MIG_001' ? 3 : 2;

    this.handleStepError(4, fallbackStep);
    this.refreshSessionData();
  }

  //GENERIC FUNCTIONS
  closeMigrationModal() {
    //se è un messaggio di warning non posso chiudere la modale ed uscire dal processo
    if (this.modalMessageType === 'info') {
      this.resetWizardState();
      // La chiusura della modale viene gestita dal componente padre
    }
  }

  closeModalOnCompleteMigration() {
    window.location.reload();
  }

  private readonly handleStepError = (
    currentIndex: number,
    fallbackStep?: number
  ): void => {
    const targetStep = fallbackStep ?? Math.max(0, currentIndex - 1);
    const normalizedTarget = Math.max(
      0,
      Math.min(targetStep, this.MigrationSteps.length - 1)
    );

    this.loading = false;
    this.resetFormsFromStep(normalizedTarget);
    this.openStep(normalizedTarget);
  };

  private resetFormsFromStep(stepIndex: number): void {
    const normalizedStep = Math.max(
      0,
      Math.min(stepIndex, this.MigrationSteps.length - 1)
    );

    if (normalizedStep <= 0) {
      this.consentAttempted = false;
      this.consentForm.reset({
        userAcceptedGeneralConditions: false,
        pin: '',
      });
    }

    if (normalizedStep <= 1) {
      this.otpAttempted = false;
      this.otpForm.reset({ otp: '' });
    }

    if (normalizedStep <= 2) {
      this.newPinAttempted = false;
      this.newPinForm.reset({
        newPin: '',
        confirmPin: '',
      });
    }

    if (normalizedStep <= 3) {
      this.passphraseAttempted = false;
      this.passphraseForm.reset({ passphrase: '' });
    }

    if (normalizedStep <= 4) {
      this.newOtpAttempted = false;
      this.newOtpForm.reset({ newOtp: '' });
    }

    this.resetOtpInput();
    this.otp.markAsPristine();
    this.otp.markAsUntouched();
    this.newOtp.markAsPristine();
    this.newOtp.markAsUntouched();
  }

  private resetWizardState(): void {
    this.resetFormsFromStep(0);
    this.MigrationSteps.forEach(step => (step.open = false));
    this.currentStep = 0;

    if (this.session) {
      this.getMigrationStep();
    } else {
      this.openStep(0);
    }
  }

  resetOtpInput() {
    this.otpService.resetOtpInputVal(true);
  }

  //START FORM-PASSPHRASE

  get passphraseFormErrors() {
    const errors = this.passphraseForm.errors;
    return errors;
  }

  onPassphraseSubmit() {}

  onConsentCheckboxChange(event: any): void {
    const checked = !!event?.target?.checked;
    this.userAcceptedGeneralConditions.setValue(checked);
    this.userAcceptedGeneralConditions.markAsTouched();
    this.userAcceptedGeneralConditions.updateValueAndValidity({
      emitEvent: false,
    });
    if (checked) {
      this.consentAttempted = false;
    }
  }

  sendOtp() {
    console.log('send otp');
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  setCF(event: any) {
    if (event.target.checked) {
      this.fPassphrase['passphrase'].setValue(this.CF);
    } else {
      this.fPassphrase['passphrase'].setValue('');
    }
  }

  //Tutto apposto è andata a buon fine la migrazione
  completeMigration() {
    //chiudiamo tutti i form
    this.openStep(-1);
    //mostriamo modal di successo
    this.openMigrationCheckMessageModal();
    this.isMigrationCompleted = true;
    this.refreshSessionData();
  }

  openMigrationCheckMessageModal() {
    this.migrationSuccessModalVisible = true;

    //timouet per chiudere la modale dopo 10 secondi (ricarica la pagina)
    setTimeout(() => {
      this.closeModalOnCompleteMigration();
    }, 10000);
  }

  onToggleMigrationSuccessModal(visible: boolean): void {
    this.migrationSuccessModalVisible = visible;
    if (!visible) {
      this.closeModalOnCompleteMigration();
    }
  }

  refreshSessionData() {
    this.sessionService.getUserSession().then(async session => {
      this.localStorageService.store('session', session);
      const certificates = await lastValueFrom(
        this.certificateService.getCertificateList()
      );
      this.certificateService.certID = certificates?.[0].certId;
      const certificate = await lastValueFrom(
        this.certificateService.getCertificate()
      );
      this.localStorageService.store('certificate', certificate);
    });
  }
}
